/**
 * Browser Web Push integration.
 *
 * Boundary contract:
 *   - enablePush(): request permission, register sw if needed, subscribe via
 *     VAPID, POST subscription to /v1/push/subscribe.
 *   - disablePush(): unsubscribe from PushManager + DELETE on the server.
 *   - pushState(): snapshot of current browser state (used by the UI toggle).
 *
 * Everything is best-effort and degrades cleanly — older browsers, denied
 * permission, missing VAPID key on the server all return without throwing.
 */
import { ApiError, api } from './api';
import { t } from './i18n';

export type PushState = 'unsupported' | 'denied' | 'granted' | 'default';

function supported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export function pushState(): PushState {
  if (!supported()) return 'unsupported';
  const perm = Notification.permission;
  if (perm === 'granted') return 'granted';
  if (perm === 'denied') return 'denied';
  return 'default';
}

/**
 * VAPID public keys come from the server as a URL-safe base64 string. The
 * PushManager wants a Uint8Array, so we convert per the standard recipe.
 */
function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

async function getRegistration(): Promise<ServiceWorkerRegistration> {
  // main.tsx registers sw.js on load — wait for it to be ready.
  const existing = await navigator.serviceWorker.getRegistration();
  if (existing) return existing;
  return navigator.serviceWorker.register('/sw.js');
}

export async function enablePush(): Promise<PushState> {
  if (!supported()) return 'unsupported';

  if (Notification.permission === 'default') {
    const result = await Notification.requestPermission();
    if (result !== 'granted') return result as PushState;
  }
  if (Notification.permission !== 'granted') return Notification.permission as PushState;

  const reg = await getRegistration();

  // Server VAPID key. If the server has not been configured we can't subscribe.
  const { key } = await api<{ key: string | null }>('/v1/push/public-key');
  // Thrown as an ApiError so the UI shows a sentence, not an internal note.
  if (!key)
    throw new ApiError(500, 'PUSH_UNAVAILABLE', t('Notifications are not available right now'));

  // Reuse existing subscription if present; PushManager.subscribe is idempotent
  // but re-running it after the user revoked + re-granted permission may fail.
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(key),
    });
  }

  const raw = sub.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
  if (!raw.endpoint || !raw.keys?.p256dh || !raw.keys?.auth) {
    throw new ApiError(500, 'PUSH_UNAVAILABLE', t('Notifications are not available right now'));
  }

  await api('/v1/push/subscribe', {
    method: 'POST',
    body: JSON.stringify({
      endpoint: raw.endpoint,
      keys: { p256dh: raw.keys.p256dh, auth: raw.keys.auth },
    }),
  });

  return 'granted';
}

export async function disablePush(): Promise<PushState> {
  if (!supported()) return 'unsupported';
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return pushState();
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    const endpoint = sub.endpoint;
    try {
      await sub.unsubscribe();
    } catch {
      /* ignore — server-side delete still happens */
    }
    try {
      await api('/v1/push/subscribe', {
        method: 'DELETE',
        body: JSON.stringify({ endpoint }),
      });
    } catch {
      /* best-effort */
    }
  }
  return pushState();
}
